import React, { memo, useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import * as Bowser from 'bowser';
import cn from 'classnames';

import LoaderIndicator from 'app/components/common/LoaderIndicator';

import { useMediaResource } from 'app/lib/MediaHooks';
import { VirtualGiftType } from 'app/store/nodes/card/interfaces';

import Title from './elements/Title';
import Expiration from './elements/Expiration';

interface IGift extends VirtualGiftType {
  buttonText?: string,
  onButtonClick?: () => void,
  onTermsClick?: () => void,
  isLoading?: boolean,
  hasTerms?: boolean,
}

const browser = Bowser.getParser(window.navigator.userAgent);
const isSafari = browser.getBrowserName(true) === 'safari';

const Gift: React.FC<IGift> = memo((props) => {
  const {
    image,
    amount,
    platform,
    currency,
    expiration,
    buttonText,
    onButtonClick,
    isLoading = false,
    onTermsClick,
    hasTerms = false,
  } = props;

  const classes = useStyles();

  const { containerRef: containerImageRef } = useMediaResource(image);

  const width = 320 * 0.95;
  const height = (width / 2) * 3;

  const bodySize = {
    width: `${width / 10}rem`,
    height: `${height / 10}rem`,
  };

  const handleButtonClick = useCallback(() => {
    onButtonClick?.();
  }, [onButtonClick]);

  return (
    <div className={classes.componentPageGift}>
      <div className={classes.body} style={{ ...bodySize }}>
        <Title className={cn(classes.title, { hidden: !expiration })} />
        {image && typeof image !== 'string' && <div className={classes.logo} ref={containerImageRef} />}
        {image && typeof image === 'string' && <div className={classes.logo}><img src={image} alt="" /></div>}
        <div className={classes.content}>
          <div className={classes.amount} style={{ visibility: amount ? 'visible' : 'hidden' }}>
            <span>{currency.symbol}</span>
            <span>{amount}</span>
          </div>
          <div className={classes.text}>
            {platform?.name}
          </div>
          <Expiration className={classes.expiration} value={expiration} />
        </div>
        <button
          type="button"
          className={classes.button}
          style={{ visibility: platform?.name ? 'visible' : 'hidden' }}
          onClick={handleButtonClick}
          disabled={isLoading}
        >
          {buttonText}
          <div className="loader"><LoaderIndicator small /></div>
        </button>
        <div className={classes.terms}>
          {hasTerms && (<button type="button" onClick={onTermsClick}>Gift Card Terms & Conditions</button>)}
        </div>
      </div>
    </div>
  );
});

const useStyles = createUseStyles({
  componentPageGift: {
    '&': {
      flex: 1,
      background: 'white',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    '& a, & button': {
      pointerEvents: 'all',
    },
    '& a, & button[disabled]': {
      borderColor: '#868686',
      pointerEvents: 'none',
      color: '#eeeeee',
    },
  },
  body: {
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    width: '95%',
    overflow: 'hidden',
    borderRadius: '1rem',
    justifyContent: 'flex-end',
    transform: isSafari ? 'translate(0, 0)' : undefined,
  },
  title: {
    '&': {
      marginTop: '0.875em',
    },
    '&.hidden': {
      visibility: 'hidden',
    },
  },
  logo: {
    '&': {
      maxWidth: '26rem',
      width: 'auto',
      height: 'auto',
      margin: '1em auto 1.375em',
    },
    '& img': {
      maxWidth: '100%',
      width: '100%',
      height: 'auto',
    },
  },
  terms: {
    '&': {
      display: 'flex',
      height: '3rem',
      justifyContent: 'center',
      alignItems: 'center',
    },
    '& > *': {
      border: 'none',
      borderBottom: '1px solid #4A4A4A',
      cursor: 'pointer',
      fontSize: '1.2em',
      fontFamily: 'Avenir Next,sans-serif',
      color: '#4A4A4A',
      outline: 'none',
      background: 'transparent',
    },
  },
  popup: {
    '&': {
      pointerEvents: 'all',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    '& > div:first-child': {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
    },
    '& > div:last-child': {
      position: 'relative',
      backgroundColor: 'white',
      margin: '0 2rem',
      padding: '0.8rem 1.8rem',
      fontSize: '1.4rem',
      height: '42rem',
      overflow: 'auto',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: 1,
  },
  amount: {
    '&': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      color: '#fa4d5a',
    },
    '& span:first-child': {
      fontFamily: 'Avenir Next,sans-serif',
      fontWeight: '700',
      fontSize: '2.07em',
      lineHeight: '1.6',
    },
    '& span:last-child': {
      lineHeight: '1.1',
      fontSize: '3.62em',
      fontWeight: 'bold',
    },
  },
  text: {
    fontSize: '1.6em',
    marginTop: '1.875em',
  },
  expiration: {
    marginTop: '0.3846em',
    marginBottom: '0.3846em',
  },
  button: {
    '&': {
      position: 'relative',
      display: 'block',
      margin: '0.625em auto 1.875em',
      padding: '0.625em 1.875em',
      cursor: 'pointer',
      background: 'transparent',
      outline: 'none',
      fontFamily: 'Avenir Next, sans-serif',
      fontSize: '1.6em',
      color: '#4A4A4A',
      border: 'max(0.1875em, 1px) solid #4A4A4A',
      borderRadius: '1.5em',
      transition: '300ms border-color ease, 300ms color ease',
    },
    '& svg': {
      display: 'block',
      width: '100%',
    },
    '& .loader': {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: '300ms opacity ease',
      opacity: 0,
    },
    '&[disabled] .loader': {
      opacity: 1,
    },
  },
});

export default Gift;
